import { memo, useCallback } from "react";
import { AlertTriangle } from "lucide-react";
import RelierEditor from "./AssociationEditors/RelierEditor";
import OrdonnerEditor from "./AssociationEditors/OrdonnerEditor";
import TexteTrouEditor from "./AssociationEditors/TexteTrouEditor";
import ClasserEditor from "./AssociationEditors/ClasserEditor";
import TrueFalseTableElement from "../Elements/TrueFalseTableElement";

const ASSOCIATION_SUBTYPES = [
    { id: 'relier', label: 'Relier' },
    { id: 'ordonner', label: 'Ordonner' },
    { id: 'texte_trou', label: 'Texte à trous' },
    { id: 'classer', label: 'Classer' },
    { id: 'tableau', label: 'Tableau' },
];

// Clés utilisées par chaque sous-type dans data
const SUBTYPE_KEYS = {
    relier: ['pairs', 'cibles_leurres'],
    ordonner: ['items'],
    texte_trou: ['phrase', 'mode', 'distracteurs', 'strict'],
    classer: ['categories'],
    tableau: [], // géré via tableElement, pas via data
};

// Vérifie si data contient des infos non vides pour un sous-type donné
const hasDataForSubType = (data, subType) => {
    if (!data) return false;
    const keys = SUBTYPE_KEYS[subType];
    if (!keys) return false;

    return keys.some(key => {
        const val = data[key];
        if (val === undefined || val === null || val === '' || val === false) return false;
        if (Array.isArray(val)) {
            if (val.length === 0) return false;
            // Vérifier que les éléments ne sont pas tous vides
            if (subType === 'relier') return val.some(p => p.source?.trim() || p.target?.trim());
            if (subType === 'ordonner') return val.some(it => it?.trim());
            if (subType === 'texte_trou' && key === 'distracteurs') return val.length > 0;
            if (subType === 'classer') return val.some(c => c.name?.trim() || c.items?.length > 0);
            return val.length > 0;
        }
        if (typeof val === 'string') return val.trim().length > 0;
        return true;
    });
};

function AssociationResponseEditor({ subType, onSubTypeChange, data, onDataChange, onValidChange, tableElement, onTableElementChange }) {

    // Vérifie si le sous-type actuel a des données à perdre
    const hasCurrentData = subType === 'tableau'
        ? Boolean(tableElement?.config?.lignes?.length)
        : hasDataForSubType(data, subType);

    const handleSubTypeClick = useCallback((newSubType) => {
        if (newSubType === subType) return;

        const doSwitch = () => {
            onDataChange({});
            // Reset tableau si on quitte le sous-type tableau
            if (subType === 'tableau' && onTableElementChange) onTableElementChange(null);
            // Initialiser tableau si on arrive sur le sous-type tableau
            if (newSubType === 'tableau' && onTableElementChange && !tableElement) {
                onTableElementChange({ type: 'table', id: Date.now(), config: { type: 'vraiFaux', titre: '', sousTitre: '', colonnes: ['Vrai', 'Faux'], lignes: [''], bonnesReponses: [0] } });
            }
            onSubTypeChange(newSubType);
        };

        if (hasCurrentData) {
            const label = ASSOCIATION_SUBTYPES.find(s => s.id === newSubType)?.label || newSubType;
            if (window.confirm(`Passer à "${label}" supprimera les données saisies. Continuer ?`)) {
                doSwitch();
            }
        } else {
            doSwitch();
        }
    }, [subType, hasCurrentData, onSubTypeChange, onDataChange, onTableElementChange, tableElement]);

    return (
        <div className="p-3 sm:p-5 rounded-2xl border border-gray-200 bg-white shadow-sm">
            <h3 className="text-xs sm:text-sm font-semibold text-gray-600 uppercase tracking-wide flex items-center gap-2 mb-3">
                <span className="w-5 h-5 rounded bg-sky-100 text-sky-800 text-[10px] font-extrabold flex items-center justify-center">2</span>
                Réponses
            </h3>

            {/* Sous-type selector */}
            <div className="flex flex-wrap gap-1.5 mb-4">
                {ASSOCIATION_SUBTYPES.map(({ id, label }) => (
                    <button
                        key={id}
                        type="button"
                        onClick={() => handleSubTypeClick(id)}
                        className={`px-2 sm:px-3 py-1.5 rounded-full text-[10px] sm:text-[11px] font-medium transition-all border ${
                            subType === id
                                ? 'border-sky-400 bg-sky-50 text-sky-700'
                                : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'
                        }`}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {/* Body — un seul sous-éditeur actif */}
            {subType === 'relier' && <RelierEditor data={data} onDataChange={onDataChange} onValidChange={onValidChange} />}
            {subType === 'ordonner' && <OrdonnerEditor data={data} onDataChange={onDataChange} onValidChange={onValidChange} />}
            {subType === 'texte_trou' && <TexteTrouEditor data={data} onDataChange={onDataChange} onValidChange={onValidChange} />}
            {subType === 'classer' && <ClasserEditor data={data} onDataChange={onDataChange} onValidChange={onValidChange} />}
            {subType === 'tableau' && (
                <div className="space-y-3">
                    <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                        <AlertTriangle size={14} className="text-amber-600 flex-shrink-0" />
                        <p className="text-xs text-amber-700">
                            Configurez le tableau ci-dessous — l'élève sélectionnera une réponse par ligne.
                        </p>
                    </div>
                    {tableElement && (
                        <TrueFalseTableElement
                            config={tableElement.config}
                            onConfigChange={(cfg) => onTableElementChange({ ...tableElement, config: cfg })}
                            onRemove={() => onTableElementChange(null)}
                            onValidChange={onValidChange}
                        />
                    )}
                </div>
            )}
        </div>
    );
}

export { ASSOCIATION_SUBTYPES, SUBTYPE_KEYS };
export default memo(AssociationResponseEditor);
